import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRightOnRectangleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import SidebarNavGroup from './SidebarNavGroup';
import navigation from '../config/navigation';

const SIDEBAR_KEY = 'cms_sidebar_collapsed';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed); } catch { /* ignore */ }
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : '??';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 bg-slate-900 text-white flex flex-col transition-all duration-200 border-r border-slate-800`}
      >
        {/* Logo */}
        <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-5'} h-16 border-b border-slate-800`}>
          {collapsed ? (
            <span className="text-lg font-bold text-blue-400">DM</span>
          ) : (
            <span className="text-lg font-bold tracking-tight">
              <span className="text-blue-400">Douro</span>
              <span className="text-slate-300"> Make</span>
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? 'px-1.5' : 'px-3'} space-y-1`}>
          {navigation
            .filter((item) => !item.adminOnly || user?.isAdmin)
            .map((item) => (
              <SidebarNavGroup key={item.name} item={item} collapsed={collapsed} />
            ))}
        </nav>

        {/* Footer: user + collapse + logout */}
        <div className="border-t border-slate-800 p-3 space-y-2">
          {/* User */}
          {!collapsed && (
            <div className="flex items-center gap-3 px-2 py-1.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold">
                {initials}
              </div>
              <span className="text-sm text-slate-300 truncate">{user?.email}</span>
            </div>
          )}
          {/* Actions row */}
          <div className={`flex ${collapsed ? 'flex-col' : ''} gap-1`}>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center justify-center p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="h-4 w-4" />
              ) : (
                <ChevronDoubleLeftIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={handleLogout}
              className={`flex items-center justify-center gap-2 p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors ${collapsed ? '' : 'flex-1'}`}
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              {!collapsed && <span className="text-sm">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6 lg:px-8 lg:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
